



















































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import { AxiosError } from 'axios';
import {
  useUpdateThreshold,
  useCreateThreshold
} from '@/composable/useWorkflowStateThreshold';
import { cloneDeep } from 'lodash';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import {
  IWorkflowStateThreshold,
  IWorkflowStateThresholdData,
  WorkflowStateThreshold
} from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import GroupAutoCompletePicker from '@/components/account/GroupAutoCompletePicker.vue';
import { requiredValidator } from '@/utils/validators';

export default defineComponent({
  name: 'WorkflowStateThresholdFormDialog',
  components: {
    ALDialog,
    ErrorAlert,
    GroupAutoCompletePicker
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    threshold: {
      type: Object as PropType<IWorkflowStateThresholdData | null>,
      default: null
    },
    workflowId: {
      type: Number,
      required: true
    },
    workflowStateId: {
      type: Number,
      required: true
    }
  },
  emits: ['update:dialog'],

  setup(props, { emit }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const thresholdData = ref<IWorkflowStateThresholdData>({});

    watch(localDialog, value => {
      if (value) {
        resetFormValidation();
        thresholdData.value = !props.threshold
          ? ({} as IWorkflowStateThresholdData)
          : cloneDeep(props.threshold!);
      }
    });

    const formRef = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () => `${props.isEdit ? 'Update' : 'Create'} state threshold`
    );

    function closeDialog() {
      clearErrors();
      localDialog.value = false;
    }

    const {
      updateThreshold,
      isUpdatingThreshold,
      updateThresholdError,
      updateThresholdErrorReset
    } = useUpdateThreshold(props.workflowId, props.workflowStateId);
    const {
      createThreshold,
      isCreatingThreshold,
      createThresholdError,
      createThresholdErrorReset
    } = useCreateThreshold(props.workflowId, props.workflowStateId);

    const localError = ref<ISystemError | null>(null);

    const loading = computed(() =>
      props.isEdit ? isCreatingThreshold.value : isUpdatingThreshold.value
    );

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = props.isEdit
          ? updateThresholdError.value
          : createThresholdError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    function clearErrors() {
      error.value = null;
      updateThresholdErrorReset();
      createThresholdErrorReset();
    }

    function resetFormValidation() {
      if (formRef.value) {
        formRef.value.resetValidation();
      }
    }

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    async function save() {
      const valid = await validateForm();

      if (valid) {
        let thresholdInstance: IWorkflowStateThreshold | null = null;

        try {
          if (
            !thresholdData.value.threshold &&
            thresholdData.value.threshold !== 0
          ) {
            // Set threshold to null if not provided
            thresholdData.value.threshold = null;
          }
          thresholdInstance = new WorkflowStateThreshold(thresholdData.value);
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (thresholdInstance != null) {
          const saveAction = props.isEdit ? updateThreshold : createThreshold;

          let payload = thresholdInstance;
          if (props.isEdit) {
            payload = diffDeep(thresholdInstance, props.threshold, true);
          }

          saveAction(payload, {
            onSuccess: () => {
              closeDialog();
            }
          });
        }
      }
    }

    return {
      localDialog,
      thresholdData,
      formRef,
      valid,
      subtitle,
      loading,
      error,
      closeDialog,
      clearErrors,
      resetFormValidation,
      save,
      requiredValidator
    };
  }
});
